'use client';

import { API_ROUTES, createQueryKey, HTTP_METHODS, useApiCall } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useQuery } from '@tanstack/react-query';
import type { WalletTransaction, WalletTransactionsResponse } from '../types';

const LATEST_TRANSACTION_SUMMARY_LIMIT = 1;

type UseLatestTransactionSummaryReturn = {
  accountSummary: WalletTransactionsResponse['accountSummary'] | undefined;
  latestTransaction: WalletTransaction | undefined;
  isLoading: boolean;
};

function useLatestTransactionSummary(): UseLatestTransactionSummaryReturn {
  const apiCall = useApiCall();
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const path = API_ROUTES.MY_ACCOUNT.WALLET.GET_TRANSACTIONS.replace(':playerId', playerId ?? '');

  const { data, isLoading } = useQuery({
    queryKey: createQueryKey(path, { limit: LATEST_TRANSACTION_SUMMARY_LIMIT }),
    queryFn: (): Promise<WalletTransactionsResponse | null> =>
      apiCall<WalletTransactionsResponse>(path, HTTP_METHODS.GET, {
        params: { limit: LATEST_TRANSACTION_SUMMARY_LIMIT },
        credentials: true,
      }),
    enabled: !!playerId,
    staleTime: 0,
  });

  return {
    accountSummary: data?.accountSummary,
    latestTransaction: data?.transactions[0],
    isLoading,
  };
}

export { useLatestTransactionSummary };
export type { UseLatestTransactionSummaryReturn };
